/**
 * The settled business rules, as named constants.
 *
 * No literal from this file may appear anywhere else in the codebase. Every
 * one of these numbers was argued over and written down; a copy of it in a
 * service is a second source of truth that will drift.
 *
 * Values marked "config" are the seed for a `config_entries` row and are read
 * at runtime through the effective dated resolver, so changing one is a data
 * change and not a deployment.
 */

import { ClaimCategory, Grade, PaymentMethod, WeightTolerance } from './enums'
import { d2j, fromJalali, j2d, toJalali } from './jalali'

/* ==========================================================================
   1. Commission and tax
   ========================================================================== */

/** ۵ در هزار of the deal value. config */
export const COMMISSION_RATE_PER_MILLE = 5

/** config. The two shares must sum to 100 or the API refuses to boot. */
export const COMMISSION_SELLER_SHARE_PCT = 50
export const COMMISSION_BUYER_SHARE_PCT = 50

/** Derived. Printed on the deal room as «۲٫۵ در هزار». */
export const COMMISSION_PER_SIDE_PER_MILLE =
  (COMMISSION_RATE_PER_MILLE * COMMISSION_SELLER_SHARE_PCT) / 100

/**
 * The matched weight, never the delivered weight. The alternative exists in
 * the type so a later true up is a config change rather than a rewrite.
 */
export const COMMISSION_BASIS: 'MATCHED' | 'DELIVERED' = 'MATCHED'

/** The first deal of each party is free. There is no one third tier. */
export const FIRST_DEAL_MULTIPLIER = 0
export const SUBSEQUENT_DEAL_MULTIPLIER = 1

/** config. No floor and no cap at launch. */
export const COMMISSION_FLOOR_RIAL: number | null = null
export const COMMISSION_CAP_RIAL: number | null = null

/** config. One band. The structure exists so a stepped rate is data. */
export const COMMISSION_TIERS: Array<{
  fromValue: number
  toValue: number | null
  ratePerMille: number
}> = [{ fromValue: 0, toValue: null, ratePerMille: COMMISSION_RATE_PER_MILLE }]

/** config. A separate line on top of the commission, never folded into it. */
export const VAT_RATE_PCT = 10

/** مهلت تسویه کارمزد, counted from the match. */
export const COMMISSION_DUE_DAYS = 2

/** Floor each party, give the odd Rial to the buyer. */
export const ROUNDING_REMAINDER_TO: 'BUYER' | 'SELLER' = 'BUYER'

/** The rate applies to the goods value before its own VAT. */
export const COMMISSION_ON_GOODS_VAT = false

export interface CommissionBreakdown {
  dealValueRial: number
  totalCommissionRial: number
  sellerCommissionRial: number
  buyerCommissionRial: number
  sellerVatRial: number
  buyerVatRial: number
  sellerPayableRial: number
  buyerPayableRial: number
  /** True when this is the party's first deal and the charge is waived. */
  sellerWaived: boolean
  buyerWaived: boolean
}

/** The four rates a commission is struck on. */
export interface CommissionRates {
  ratePerMille: number
  sellerSharePct: number
  buyerSharePct: number
  vatRatePct: number
}

/** The one commission calculation in the system. */
export function computeCommission(
  dealValueRial: number,
  options: { sellerFirstDeal?: boolean; buyerFirstDeal?: boolean } = {},
  /**
   * The rates in force, as the operator's commission settings name them.
   * Absent, the platform's published constants, so every caller that never
   * read the settings still gets the canonical figures.
   */
  rates: Partial<CommissionRates> = {},
): CommissionBreakdown {
  const { sellerFirstDeal = false, buyerFirstDeal = false } = options
  const ratePerMille = rates.ratePerMille ?? COMMISSION_RATE_PER_MILLE
  const sellerSharePct = rates.sellerSharePct ?? COMMISSION_SELLER_SHARE_PCT
  const buyerSharePct = rates.buyerSharePct ?? COMMISSION_BUYER_SHARE_PCT
  const vatRatePct = rates.vatRatePct ?? VAT_RATE_PCT

  const total = (dealValueRial * ratePerMille) / 1000
  const sellerRaw = Math.floor((total * sellerSharePct) / 100)
  const buyerRaw =
    ROUNDING_REMAINDER_TO === 'BUYER'
      ? Math.round(total) - sellerRaw
      : Math.floor((total * buyerSharePct) / 100)

  const sellerCommission = sellerFirstDeal ? sellerRaw * FIRST_DEAL_MULTIPLIER : sellerRaw
  const buyerCommission = buyerFirstDeal ? buyerRaw * FIRST_DEAL_MULTIPLIER : buyerRaw

  const sellerVat = Math.round((sellerCommission * vatRatePct) / 100)
  const buyerVat = Math.round((buyerCommission * vatRatePct) / 100)

  return {
    dealValueRial,
    totalCommissionRial: Math.round(total),
    sellerCommissionRial: sellerCommission,
    buyerCommissionRial: buyerCommission,
    sellerVatRial: sellerVat,
    buyerVatRial: buyerVat,
    sellerPayableRial: sellerCommission + sellerVat,
    buyerPayableRial: buyerCommission + buyerVat,
    sellerWaived: sellerFirstDeal,
    buyerWaived: buyerFirstDeal,
  }
}

/* ==========================================================================
   2. Matching and validity
   ========================================================================== */

/** config. Asia/Tehran. */
export const MATCHING_WINDOW_START = '09:00'
export const MATCHING_WINDOW_END = '17:00'

/**
 * config. Every day is a trading day.
 *
 * So the window is the only constraint, and a weekly closure is a config
 * change on this list rather than a rewrite: `SESSION.closedWeekdays` in
 * trading-clock.ts reads it, and the clock already handles closed days.
 */
export const MATCHING_DAYS = ['SAT', 'SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI'] as const

/**
 * The validity counter runs only inside the window. Outside it neither party
 * can act, so burning validity would silently shorten every listing that
 * spans a night.
 */
export const VALIDITY_CLOCK_FROZEN_OUTSIDE_WINDOW = true

/** config. One matching hour floor, twenty four hours seeded as the ceiling. */
export const VALIDITY_MIN_MINUTES = 60
export const VALIDITY_MAX_MINUTES_DEFAULT = 1440

/** Best price first, then earliest valid timestamp, then the persisted order. */
export const BID_PRIORITY = ['PRICE_DESC', 'PLACED_AT_ASC', 'SEQ_ASC'] as const

/** Sell side matches at or above base; buy side matches at or below base. */
export const AUTO_MATCH_THRESHOLD = 'AT_OR_ABOVE_BASE'

/** They enter the book and are visible; they never auto match. */
export const SUB_BASE_BIDS_ENTER_BOOK = true

/** A qualifying bid at or above حداقل قابل خرید must be sold. */
export const PARTIAL_FILL_MANDATORY_ABOVE_MIN_LOT = true

/**
 * A bid larger than the remaining quantity is skipped entirely, even at a
 * better price, unless its bidder said on the bid that they take less. Then
 * it takes what is left (`partialFillKg`). A bidder who did not say so is
 * never handed a quantity they did not ask for, which is why the book keeps
 * the row in sight with its status rather than hiding it.
 */
export const SKIP_BID_LARGER_THAN_REMAINDER = true

/**
 * The weight a bid that accepts a partial fill takes when it is larger than
 * what is left: the remainder, put on the listing's own step, or nothing at
 * all when that falls under the minimum lot. The same arithmetic caps the
 * weight a lister may type when they take less than a bid by hand.
 */
export function partialFillKg(
  remainingKg: number,
  bidKg: number,
  minLotKg: number,
  stepKg: number | null | undefined,
): number | null {
  const capped = Math.min(remainingKg, bidKg)
  const step = stepKg ?? 0
  const legal = step > 0 ? Math.floor(capped / step) * step : capped
  return legal > 0 && legal >= minLotKg ? legal : null
}

export const PRO_RATA_SPLIT_ON_EQUAL_PRICE = false

/** The remainder stays on the board until the original validity expires. */
export const REMAINDER_STAYS_ON_BOARD = true
export const REDUCED_QUANTITY_NEEDS_BIDDER_CONFIRM = true
export const REMAINDER_BELOW_MIN_LOT_CLOSES_LISTING = true

/** One hour of session time before expiry. Not thirty minutes. */
export const PRE_EXPIRY_NUDGE_MINUTES = 60

export const MANUAL_MATCH_ALLOWED_ANY_TIME_IN_VALIDITY = true
export const RELIST_AFTER_CANCEL_REQUIRES_NEW_LISTING = true

/** Cancellable before or after the window, never inside it. */
export const CANCEL_INSIDE_MATCHING_HOURS_ALLOWED = false

/** A shortening may not cut the remaining validity below this share of it. */
export const VALIDITY_SHORTEN_MIN_REMAINING_PCT = 20
export const VALIDITY_EXTEND_ALLOWED = false
export const TICK_SIZE_ENFORCED = true
export const QUANTITY_STEP_SOURCE = 'lotStepKg'

/* ==========================================================================
   3. Operator suggestion weights
   ========================================================================== */

/** The weighted candidate model on the operator desk. Sums to 100. */
export const SUGGESTION_WEIGHTS = {
  PRODUCT: 20,
  QUALITY: 15,
  QUANTITY: 15,
  PRICE: 20,
  LOGISTICS_PAYMENT: 30,
  /** Present at zero so giving them weight is a config change, not a release. */
  BRAND: 0,
  HISTORY: 0,
  ANCILLARY: 0,
  DESCRIPTION: 0,
} as const

/* ==========================================================================
   4. Identity, one time codes and sessions
   ========================================================================== */

export const OTP_LENGTH = 6
export const OTP_TTL_SECONDS = 120
export const OTP_MAX_ATTEMPTS = 5
export const OTP_RESEND_INTERVAL_SECONDS = 60
export const OTP_LOCKOUT_MINUTES = 15
export const OTP_MAX_REQUESTS_PER_HOUR = 5

export const PASSWORD_MIN_LENGTH = 8
export const ACCESS_TOKEN_TTL_MINUTES = 15
export const REFRESH_TOKEN_TTL_DAYS = 30
export const ADMIN_SESSION_REQUIRES_2FA = true

export const RE_KYC_MONTHS = 12
export const KYC_DOC_MAX_BYTES = 10_485_760
export const KYC_DOC_MIME_ALLOWLIST = ['application/pdf', 'image/jpeg', 'image/png'] as const

/**
 * The single policy point for identity masking. Until it fires the
 * counterparty is named by its role alone, «فروشنده» or «خریدار», with its
 * activity kind and grade as separate fields. Never a real name, a province,
 * a deal count, its permanent party number, or the order it stands behind.
 * The API builds every such label in one place.
 */
export const IDENTITY_REVEAL_TRIGGER = 'COMMISSION_SETTLED'

/* ==========================================================================
   5. Grade
   ========================================================================== */

export const GRADE_BAND_GOLD_MIN = 80
export const GRADE_BAND_SILVER_MIN = 60
export const GRADE_BAND_BRONZE_MIN = 50
export const GRADE_BAND_LOWER_INCLUSIVE = true
export const GRADE_RECOMPUTE_CADENCE = 'MONTHLY'
export const GRADE_BOOTSTRAP_IS_MANUAL = true
export const GRADE_SCALE_MIN = 0
export const GRADE_SCALE_MAX = 100

export function gradeForScore(score: number): Grade {
  if (score >= GRADE_BAND_GOLD_MIN) return Grade.GOLD
  if (score >= GRADE_BAND_SILVER_MIN) return Grade.SILVER
  if (score >= GRADE_BAND_BRONZE_MIN) return Grade.BRONZE
  return Grade.NONE
}

export interface GradeIndicatorSpec {
  key: string
  nameFa: string
  /** Null means the indicator is tracked but carries no weight. */
  weight: number | null
}

/** Thirteen weighted indicators summing to 100, plus one unweighted. */
export const SELLER_GRADE_INDICATORS: GradeIndicatorSpec[] = [
  { key: 'soldVolume', nameFa: 'مقدار فروش', weight: 12 },
  { key: 'paymentTermsFlexibility', nameFa: 'انعطاف در شرایط پرداخت', weight: 12 },
  { key: 'salesGrowth', nameFa: 'نرخ رشد فروش', weight: 12 },
  { key: 'offeredVolume', nameFa: 'مقدار عرضه', weight: 10 },
  { key: 'standingHistory', nameFa: 'اعتبار و سابقه فروشنده', weight: 10 },
  { key: 'offerCount', nameFa: 'تعداد عرضه', weight: 7 },
  { key: 'priceCompetitiveness', nameFa: 'قیمت و رقابت‌پذیری', weight: 7 },
  { key: 'deliveryPerformance', nameFa: 'عملکرد تحویل', weight: 7 },
  { key: 'upheldClaims', nameFa: 'میزان شکایت تأیید شده', weight: 7 },
  { key: 'technicalConformity', nameFa: 'انطباق با مشخصات فنی اعلام شده', weight: 7 },
  { key: 'responsiveness', nameFa: 'پاسخگویی', weight: 4 },
  { key: 'isProducer', nameFa: 'تولیدی', weight: 3 },
  { key: 'profileCompleteness', nameFa: 'تکمیل اطلاعات پروفایل', weight: 2 },
  { key: 'preExpiryCancel', nameFa: 'لغو سفارش قبل از پایان اعتبار', weight: null },
]

/** Thirteen weighted indicators summing to 100. */
export const BUYER_GRADE_INDICATORS: GradeIndicatorSpec[] = [
  { key: 'purchasedVolume', nameFa: 'مقدار خرید', weight: 11 },
  {
    key: 'bidPriceCompetitiveness',
    nameFa: 'قیمت و رقابت‌پذیری قیمت‌های پیشنهادی',
    weight: 11,
  },
  { key: 'paymentPunctuality', nameFa: 'خوش‌حسابی و پرداخت به‌موقع', weight: 11 },
  { key: 'upheldClaims', nameFa: 'میزان شکایت تأیید شده', weight: 11 },
  { key: 'purchaseGrowth', nameFa: 'نرخ رشد خرید', weight: 11 },
  { key: 'dealsParticipated', nameFa: 'تعداد معاملات انجام شده', weight: 10 },
  { key: 'standingHistory', nameFa: 'اعتبار و سابقه خریدار', weight: 7 },
  { key: 'responsiveness', nameFa: 'پاسخگویی', weight: 7 },
  { key: 'demandedVolume', nameFa: 'مقدار تقاضا', weight: 5 },
  { key: 'demandCount', nameFa: 'تعداد تقاضا', weight: 5 },
  { key: 'purchaseTermsFlexibility', nameFa: 'انعطاف در شرایط خرید', weight: 5 },
  { key: 'profileCompleteness', nameFa: 'تکمیل اطلاعات پروفایل', weight: 3 },
  { key: 'isProducer', nameFa: 'تولیدی', weight: 3 },
]

/** Lifetime upheld claims map to an indicator score on this ladder. */
export const UPHELD_CLAIM_SCORE_LADDER: Record<number, number> = {
  0: 100,
  1: 80,
  2: 60,
  3: 40,
  4: 20,
  5: 0,
}

export interface GradePrivileges {
  validityEditsPerListing: number
  basePriceEditsPerListing: number
  /** Null means unlimited. */
  ordersPerWeek: number | null
  tonnagePerWeekKg: number | null
  preSalePerMonth: number | null
  /** فروش اعتباری. Null means unlimited, zero closes it. */
  creditSalePerMonth: number | null
  /** LC و ضمانت‌نامه بانکی, which the two lowest bands may not pledge. */
  mayPledgeCreditInstrument: boolean
  deleteBeforeExpiry: boolean
  bidsPerAuction: number | null
  bidPriceChangeAnyMoment: boolean
  bidPriceChangeOnceBeforeExpiry: boolean
  creditPurchasePerMonth: number | null
  mayList: boolean
  /** Measured in DEALS with unpaid commission, never in money. */
  commissionDebtLimitDeals: number
}

export const GRADE_PRIVILEGES: Record<Grade, GradePrivileges> = {
  GOLD: {
    validityEditsPerListing: 3,
    basePriceEditsPerListing: 2,
    ordersPerWeek: null,
    tonnagePerWeekKg: null,
    preSalePerMonth: null,
    creditSalePerMonth: null,
    mayPledgeCreditInstrument: true,
    deleteBeforeExpiry: true,
    bidsPerAuction: null,
    bidPriceChangeAnyMoment: true,
    bidPriceChangeOnceBeforeExpiry: false,
    creditPurchasePerMonth: null,
    mayList: true,
    commissionDebtLimitDeals: 3,
  },
  SILVER: {
    validityEditsPerListing: 1,
    basePriceEditsPerListing: 1,
    ordersPerWeek: 4,
    tonnagePerWeekKg: 50_000,
    preSalePerMonth: 1,
    creditSalePerMonth: 1,
    mayPledgeCreditInstrument: true,
    deleteBeforeExpiry: false,
    bidsPerAuction: 3,
    bidPriceChangeAnyMoment: false,
    bidPriceChangeOnceBeforeExpiry: true,
    creditPurchasePerMonth: 1,
    mayList: true,
    commissionDebtLimitDeals: 2,
  },
  BRONZE: {
    validityEditsPerListing: 0,
    basePriceEditsPerListing: 0,
    ordersPerWeek: 2,
    tonnagePerWeekKg: 20_000,
    preSalePerMonth: 0,
    creditSalePerMonth: 0,
    mayPledgeCreditInstrument: false,
    deleteBeforeExpiry: false,
    bidsPerAuction: 2,
    bidPriceChangeAnyMoment: false,
    bidPriceChangeOnceBeforeExpiry: false,
    creditPurchasePerMonth: 0,
    mayList: true,
    commissionDebtLimitDeals: 1,
  },
  NONE: {
    validityEditsPerListing: 0,
    basePriceEditsPerListing: 0,
    ordersPerWeek: 0,
    tonnagePerWeekKg: 0,
    preSalePerMonth: 0,
    creditSalePerMonth: 0,
    mayPledgeCreditInstrument: false,
    deleteBeforeExpiry: false,
    bidsPerAuction: 2,
    bidPriceChangeAnyMoment: false,
    bidPriceChangeOnceBeforeExpiry: false,
    creditPurchasePerMonth: 0,
    mayList: false,
    commissionDebtLimitDeals: 1,
  },
}

/* ==========================================================================
   6. Penalties and discipline
   ========================================================================== */

/** Cancelling after the match: a ban, one percent, both commissions, one grade. */
export const POST_MATCH_CANCEL_PENALTY_PCT = 1

/**
 * The share of a collected fine that reaches the wronged party as «جبران
 * خسارت». The platform keeps the rest. Parametric on the commission config,
 * so the split is a data change; this is the seed.
 */
export const CANCEL_PENALTY_COMPENSATION_SHARE_PCT = 50
export const POST_MATCH_CANCEL_IMMEDIATE_BAN = true
export const POST_MATCH_CANCEL_PAYS_BOTH_COMMISSIONS = true
export const POST_MATCH_CANCEL_GRADE_DROP = 1

/** Post match cancellation is an administrative act through a support ticket. */
export const POST_MATCH_CANCEL_IS_ADMIN_ONLY = true

export const CLAIMS_BEFORE_BLOCK = 5

/* ==========================================================================
   7. Delivery, weight and claims
   ========================================================================== */

/**
 * The claim admissibility band. A variance inside one percent is not a
 * claim. This is NOT the listing tolerance enum and the two are never
 * conflated: the enum is what the seller promised, this is what the platform
 * will hear an argument about.
 */
export const NO_CLAIM_BAND_PCT = 1

export const WEIGHT_TOLERANCE_NUMERIC: Record<WeightTolerance, { minPct: number; maxPct: number }> =
  {
    UNDER_5: { minPct: 0, maxPct: 5 },
    EXACT_5: { minPct: 5, maxPct: 5 },
    FIVE_TO_TEN: { minPct: 5, maxPct: 10 },
  }

/** A third weighbridge is called when the two declarations disagree. */
export const THIRD_WEIGHBRIDGE_TRIGGER_PCT = 1
/**
 * How long a category may be claimed for, in days from تاریخ حمل.
 *
 * `null` is a category with no deadline at all, which is two of the seven and
 * is not the same as a long one: a money case and a fraud case stay open
 * however much time has passed, so the window gate never closes them.
 *
 * Every clock runs from the ship date the SELLER records at delivery
 * declaration, not from delivery and not from acceptance. That is what makes
 * the one day transport window sharp, and it is why the case panel always
 * prints whose date it is.
 */
export const CLAIM_WINDOW_DAYS: Record<ClaimCategory, number | null> = {
  QUANTITY: 2,
  QUALITY: 7,
  DAMAGE_PACKAGING: 2,
  DELIVERY_TRANSPORT: 1,
  PAYMENT: null,
  WARRANTY_DOCUMENTS: 30,
  FRAUD: null,
}

/**
 * The same windows in hours, which is the unit the console sets them in
 * («شکایت» in the configuration). These are the seeds; the value in force is
 * the console's. A whole number of days reads exactly as the day count did.
 */
export const CLAIM_WINDOW_HOURS: Record<ClaimCategory, number | null> = Object.fromEntries(
  Object.entries(CLAIM_WINDOW_DAYS).map(([category, days]) => [category, days === null ? null : days * 24]),
) as Record<ClaimCategory, number | null>

/** The categories the console can set a window for: the five that have one. */
export const DATED_CLAIM_CATEGORIES = (Object.keys(CLAIM_WINDOW_DAYS) as ClaimCategory[]).filter(
  (category) => CLAIM_WINDOW_DAYS[category] !== null,
)

/** The console parameter that holds a category's window, in hours. */
export function claimWindowParamKey(category: ClaimCategory): string {
  return `claims.windowHours.${category}`
}

/**
 * The instant a filing window closes.
 *
 * The seller records a ship date and no hour, so the clock starts as that
 * day ends in Tehran. A window of whole days therefore closes where the day
 * count always did: two days from a ship date on the tenth admit a case to
 * the last minute of the twelfth. The case gate and the claim form both read
 * this one instant, so the form never offers what the gate then refuses.
 */
export function claimWindowEndsAt(shipDate: Date, hours: number): Date {
  const { jy, jm, jd } = toJalali(shipDate)
  const next = d2j(j2d(jy, jm, jd) + 1)
  return new Date(fromJalali(next.jy, next.jm, next.jd).getTime() + hours * 3_600_000)
}

/**
 * Whether a deal shipped on `shipDate` can still be complained about under
 * any category that has a deadline. The two categories without one stay open
 * for ever and do not count, or the answer would never be no.
 */
export function claimWindowOpen(
  shipDate: Date | null | undefined,
  windows: Record<ClaimCategory, number | null>,
  at: Date,
): boolean {
  if (!shipDate) return true
  return DATED_CLAIM_CATEGORIES.some((category) => {
    const hours = windows[category]
    return hours === null || at.getTime() < claimWindowEndsAt(shipDate, hours).getTime()
  })
}

/* ==========================================================================
   8. Payment schedule
   ========================================================================== */

/** Pre filled split on the instalment schedule, branch A. */
export const INSTALMENT_DEFAULT_SPLIT = [
  { label: 'پیش پرداخت قبل از حمل', pct: 30 },
  { label: 'پس از حمل', pct: 70 },
] as const

/** پیش فروش is cash only and the deal stays open until delivery. */
export const PRE_TRADE_IS_CASH_ONLY = true
export const PAYMENT_METHODS_WITH_SCHEDULE: PaymentMethod[] = [
  PaymentMethod.CREDIT,
  PaymentMethod.CREDIT_CASH,
]

/* ==========================================================================
   8b. How a member pays the platform
   ========================================================================== */

/**
 * The three ways money reaches the platform, in the order the system offers
 * them: the member's own wallet first, then the online card gateway, then a
 * transfer to the platform's bank account. Each is switched and bounded by
 * staff in the console (`payment.<method>.*`), not here.
 */
export const PLATFORM_PAYMENT_METHODS = ['wallet', 'gateway', 'bankTransfer'] as const
export type PlatformPaymentMethod = (typeof PLATFORM_PAYMENT_METHODS)[number]

export const PLATFORM_PAYMENT_METHOD_FA: Record<PlatformPaymentMethod, string> = {
  wallet: 'کیف پول',
  gateway: 'درگاه پرداخت اینترنتی',
  bankTransfer: 'واریز بانکی',
}

/** When a method takes an amount: always, up to a ceiling, or only above a floor. */
export const PAYMENT_AMOUNT_RULES = ['ALWAYS', 'UP_TO', 'ABOVE'] as const
export type PaymentAmountRule = (typeof PAYMENT_AMOUNT_RULES)[number]

export const PAYMENT_AMOUNT_RULE_FA: Record<PaymentAmountRule, string> = {
  ALWAYS: 'همه مبالغ',
  UP_TO: 'تا مبلغ',
  ABOVE: 'بیشتر از مبلغ',
}

export interface PaymentMethodRule {
  enabled: boolean
  rule: PaymentAmountRule
  /** The border, in rial. «تا» includes it; «بیشتر از» does not. Unread for ALWAYS. */
  amountRial: number
}

export const PAYMENT_METHOD_RULE_SEED: Record<PlatformPaymentMethod, PaymentMethodRule> = {
  wallet: { enabled: true, rule: 'ALWAYS', amountRial: 0 },
  gateway: { enabled: true, rule: 'UP_TO', amountRial: 5_000_000_000 },
  bankTransfer: { enabled: true, rule: 'ALWAYS', amountRial: 0 },
}

/** Whether a method, as staff set it, takes this amount. */
export function paymentMethodTakes(rule: PaymentMethodRule, amountRial: number): boolean {
  if (!rule.enabled) return false
  if (rule.rule === 'UP_TO') return amountRial <= rule.amountRial
  if (rule.rule === 'ABOVE') return amountRial > rule.amountRial
  return true
}

/**
 * The smallest amount a card gateway takes, in rial (the Shaparak floor).
 * Below it an invoice goes to the next method and a top up is refused.
 */
export const GATEWAY_MIN_RIAL = 10_000

/**
 * The amounts no method outside the wallet would take, as one `[from, to]`
 * band in rial (`to` null for «and above»), or null when every amount has a
 * way in. The wallet is left out: it pays only what is already in it, and
 * the money gets there through the other two.
 */
export function paymentCoverageGap(
  rules: Record<PlatformPaymentMethod, PaymentMethodRule>,
): { fromRial: number; toRial: number | null } | null {
  const outside = [rules.gateway, rules.bankTransfer].filter((r) => r.enabled)
  if (outside.some((r) => r.rule === 'ALWAYS')) return null
  const ceilings = outside.filter((r) => r.rule === 'UP_TO').map((r) => r.amountRial)
  const floors = outside.filter((r) => r.rule === 'ABOVE').map((r) => r.amountRial)
  const reach = ceilings.length ? Math.max(...ceilings) : 0
  const from = floors.length ? Math.min(...floors) : null
  if (from === null) return { fromRial: reach, toRial: null }
  if (from <= reach) return null
  return { fromRial: reach, toRial: from }
}

/* ==========================================================================
   9. Boot time assertions
   ========================================================================== */

/**
 * Called once at API start. These are the invariants that make the rest of
 * the file safe to trust; a mis-seeded config that breaks one of them should
 * stop the process rather than quietly charge the wrong commission.
 */
export function assertRuleInvariants(): void {
  const problems: string[] = []

  if (COMMISSION_SELLER_SHARE_PCT + COMMISSION_BUYER_SHARE_PCT !== 100) {
    problems.push('commission shares do not sum to 100')
  }

  const sellerWeights = SELLER_GRADE_INDICATORS.reduce((s, i) => s + (i.weight ?? 0), 0)
  if (sellerWeights !== 100) problems.push(`seller grade weights sum to ${sellerWeights}, not 100`)

  const buyerWeights = BUYER_GRADE_INDICATORS.reduce((s, i) => s + (i.weight ?? 0), 0)
  if (buyerWeights !== 100) problems.push(`buyer grade weights sum to ${buyerWeights}, not 100`)

  const suggestion = (Object.values(SUGGESTION_WEIGHTS) as number[]).reduce((s, w) => s + w, 0)
  if (suggestion !== 100) problems.push(`suggestion weights sum to ${suggestion}, not 100`)

  if (GRADE_BAND_GOLD_MIN <= GRADE_BAND_SILVER_MIN || GRADE_BAND_SILVER_MIN <= GRADE_BAND_BRONZE_MIN) {
    problems.push('grade bands are not strictly descending')
  }

  if (problems.length) {
    throw new Error(`business rule invariants violated:\n  ${problems.join('\n  ')}`)
  }
}

export interface GradeLimitRow {
  /** سفارش در هفته */
  ordersPerWeek: number
  /** حجم در هفته، تن */
  volumePerWeekTonnes: number
  /** حد اعتبار کارمزد، معامله */
  commissionCreditDeals: number
  /** قیمت در هر حراج */
  pricesPerAuction: number
  /** ویرایش زمان اعتبار، نوبت در هر عرضه */
  validityEditsPerListing: number
  /** ویرایش قیمت پایه، نوبت در هر عرضه */
  basePriceEditsPerListing: number
}

/**
 * The table `38-activated` prints when آریا فلز is granted نقره‌ای at step 10,
 * and the table `51-offer-edit` reads for طلایی. One row per grade, referenced
 * by every account: an account's `caps` are this row, not a per account edit.
 */
export const GRADE_LIMITS: Record<Grade, GradeLimitRow> = {
  NONE: {
    ordersPerWeek: 1,
    volumePerWeekTonnes: 10,
    commissionCreditDeals: 0,
    pricesPerAuction: 1,
    validityEditsPerListing: 0,
    basePriceEditsPerListing: 0,
  },
  BRONZE: {
    ordersPerWeek: 2,
    volumePerWeekTonnes: 25,
    commissionCreditDeals: 1,
    pricesPerAuction: 2,
    validityEditsPerListing: 0,
    basePriceEditsPerListing: 0,
  },
  SILVER: {
    ordersPerWeek: 4,
    volumePerWeekTonnes: 50,
    commissionCreditDeals: 2,
    pricesPerAuction: 3,
    validityEditsPerListing: 1,
    basePriceEditsPerListing: 1,
  },
  GOLD: {
    ordersPerWeek: 10,
    volumePerWeekTonnes: 200,
    commissionCreditDeals: 3,
    pricesPerAuction: 5,
    validityEditsPerListing: 2,
    basePriceEditsPerListing: 2,
  },
}

/** The four limits a party document carries, read off the grade's row above. */
export function gradeCaps(grade: Grade | null): {
  ordersPerWeek: number
  volumePerWeekTonnes: number
  commissionCreditDeals: number
  pricesPerAuction: number
} {
  const row = GRADE_LIMITS[grade ?? Grade.NONE]
  return {
    ordersPerWeek: row.ordersPerWeek,
    volumePerWeekTonnes: row.volumePerWeekTonnes,
    commissionCreditDeals: row.commissionCreditDeals,
    pricesPerAuction: row.pricesPerAuction,
  }
}
